import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { BusinessBasics } from "@/model/interface/Onboarding";
import { BUSINESS_TYPE_OPTIONS } from "@/enum/BusinessType";

interface BusinessBasicsStepProps {
  value: BusinessBasics;
  onChange: (next: BusinessBasics) => void;
}

const TEAM_SIZES = ["Just me", "2-5", "6-20", "21-50", "51+"];
const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
];
const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
];

export default function BusinessBasicsStep({ value, onChange }: BusinessBasicsStepProps) {
  const update = (patch: Partial<BusinessBasics>) => {
    onChange({ ...value, ...patch });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">Tell us about your business</h2>
        <p className="text-sm text-muted-foreground">
          The basics so we can configure your workspace.
        </p>
      </div>

      <div className="grid gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="orgName">Business name</Label>
          <Input
            id="orgName"
            value={value.orgName}
            onChange={(e) => update({ orgName: e.target.value })}
            placeholder="Acme Real Estate"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="businessType">Business type</Label>
          <Select
            value={value.businessType}
            onValueChange={(v: string) => update({ businessType: v })}
          >
            <SelectTrigger id="businessType">
              <SelectValue placeholder="Choose…" />
            </SelectTrigger>
            <SelectContent>
              {BUSINESS_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="teamSize">Team size</Label>
            <Select value={value.teamSize} onValueChange={(v: string) => update({ teamSize: v })}>
              <SelectTrigger id="teamSize">
                <SelectValue placeholder="Choose…" />
              </SelectTrigger>
              <SelectContent>
                {TEAM_SIZES.map((size) => (
                  <SelectItem key={size} value={size}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone">Phone (optional)</Label>
            <Input
              id="phone"
              type="tel"
              value={value.phone}
              onChange={(e) => update({ phone: e.target.value })}
              placeholder="+1 555 123 4567"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="country">Country</Label>
            <Select value={value.country} onValueChange={(v: string) => update({ country: v })}>
              <SelectTrigger id="country">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="timezone">Timezone</Label>
            <Select value={value.timezone} onValueChange={(v: string) => update({ timezone: v })}>
              <SelectTrigger id="timezone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="marketFocus">Primary market or geography (optional)</Label>
          <Input
            id="marketFocus"
            value={value.marketFocus}
            onChange={(e) => update({ marketFocus: e.target.value })}
            placeholder="Phoenix metro, Texas, etc."
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="useCase">What are you hoping to accomplish? (optional)</Label>
          <Textarea
            id="useCase"
            rows={3}
            value={value.useCase}
            onChange={(e) => update({ useCase: e.target.value })}
            placeholder="Centralize my leads, automate follow-up, etc."
          />
        </div>
      </div>
    </div>
  );
}
